import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { useParams } from "react-router-dom";
import { Localized, useLocalization } from "@fluent/react";
import { unwrapResult } from "@reduxjs/toolkit";
import { ErrorContainer } from "../../../app/ErrorContainer";
import { LoadingContainer } from "../../../app/LoadingContainer";
import { AppBar, Box, Button, Container, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Stack, Tab, TextField } from "@mui/material";
import { LoadingButton, TabContext, TabList, TabPanel } from "@mui/lab";
import { DocumentDetailsPanel } from "../components/DocumentDetailsPanel";
import { DocumentToolbarMenu } from "../components/DocumentToolbarMenu";
import { UrsDocumentToolbarMenu } from "../components/UrsDocumentToolbarMenu";
import { WorkflowActionRunner } from "../components/WorkflowActionRunner";
import { ActionTitle } from "../components/WorkflowActionUtils";
import { createDocSection, DocumentWorkflowActions, loadDocument, loadDocumentActions, loadDocumentSections } from "../documentsApi";
import { DocumentFormHeader } from "../components/DocumentFormHeader";
import { useNavigate } from "react-router-dom";
import { loadManyBlobs, remarksRequiredForDocumentStatus } from "../../documents/attachementsSlice";
import { Attachements } from "../../urs/Attachements";
import { BlobResourceType } from "../blobResourceTypes";
import TestTable from "./TestTable";
import { docActionsSelectors, docWfActionsSelectors, isDocEditable, sectionSelectors, selectDocument } from "../documentSlice";
import { APIError, AppId } from "../../../app/appTypes";
import { ServerErrorMsg } from "../../../app/ServerErrorMsg";
import { DocSectionType } from "../documentTypes";
import { impossible } from "../../../app/Utils";
import { SectionDefinition, getSectionDefinitionsForDocumentType, sectionTypeValueToL10Code, SECTION_TYPE_LABELS, sanitizeSectionCode } from "./DocumentSectionRules";
import { DocumentContent } from "../DocumentContent";
import { TeamMemberView } from "../components/TeamMemberView";
import { loadDocComments } from "../commentsAPI";
import DocumentHistoryView from "../documentHistory/DocumentHistoryView";
import { SaveButton } from "../../../app/components/SaveButton";
import { CancelButton } from "../../../app/components/CancelButton";
import { DocumentRejectPanel } from "../components/DocumentRejectPanel";
import { DocumentNewVersionDialog } from "../components/DocumentNewVersionDialog";
import { RiskAnalysisSection } from "../RiskAnalysisSection";
import { loadDocumentValidatedObject } from "../../project/validationProcess/validatedObjectApi";
import { Device } from "../../device/deviceSlice";
import { DeviceGeneralInfo, DeviceHead } from "../../device/DeviceForm";
import { ComputerizedSystemHead } from "../../computerizedSystems/ComputerizedSystemForm";
import { ComputerizedSystemOverviewPanel } from "../../computerizedSystems/panels/ComputerizedSystemOverviewPanel";
import { ComputerizedSystem } from "../../computerizedSystems/computerizedSystemsApi";
import { RequirementsSection } from "../documentRequirements/RequirementsSection";
import { UrsOffers } from "../../urs/UrsOffers";
import { WizardPublishDialog } from "../../urs/dialogs/WizardPublishDialog";
import { publishAction } from "../../urs/UrsSlice";
import { showError, showSuccess } from "../../notifications/notificationsSlice";
import { DocumentQMSDetails } from "../components/DocumentQMSDetails";
import { loadURSComments } from "../../urs/URSCommentSlice";
import { loadAccessRequests, PubAccessRequest } from "../publicAccessApi";
import { EntityBreadcrumbs } from "../../../app/components/EntityBreadcrumbs";
import { DocumentTeamMembersView } from "../workflow/DocumentTeamMembersView";
import { FormDefinitionPicker } from "../sections/formSection/FormDefinitionPicker";
import { selectFormDefinitions } from "../sections/formSection/formSectionSlice";
import { createFormSection } from "../sections/formSection/formSectionApi";

export type DocumentFormTabs = "rejection" | "content" | "attachments" | "team" | "ra" | "requirements" | "wflteam"

export const DocumentForm = () => {
    return <DocumentGuard>
        <Dialogs>
            <DocumentFormInternal />
        </Dialogs>
    </DocumentGuard>
}

type DocDialog
    = { tag: 'none' }
    | { tag: 'add-section', parentSectionId: AppId | null }

export const DialogContext = createContext<(d: DocDialog) => void>(() => { })

export const Dialogs = ({ children }: { children: ReactNode }) => {
    const [dialog, setDialog] = useState<DocDialog>({ tag: 'none' })
    const setDialogContext = useCallback((d: DocDialog) => {
        setDialog(d)
    }, [])

    let dialogContent: ReactNode | null = null
    switch (dialog.tag) {
        case 'none':
            dialogContent = null
            break
        case 'add-section':
            dialogContent = <AddSectionDialog parentSectionId={dialog.parentSectionId} />
            break

    }

    return <DialogContext.Provider value={setDialogContext}>
        <>
            {children}
            {dialogContent}
        </>
    </DialogContext.Provider>
}



const AddSectionDialog = (props: { parentSectionId: AppId | null }) => {
    const { parentSectionId } = props
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()
    const dialog = useContext(DialogContext)

    const docState = useAppSelector(selectDocument)?.doc ?? impossible()
    const allSections = useAppSelector(state => sectionSelectors.selectAll(state))

    const { id: documentId } = docState.header

    const [name, setName] = useState('')
    const [code, setCode] = useState('')
    const [sectionType, setSectionType] = useState<DocSectionType>('GROUP')
    // Only templates expose the code {{SectionCode:CODE}} report templates use; see SectionContent.
    const canSetCode = docState.header.isTemplate === true && sectionType === 'GROUP'
    const [formDefinitionId, setFormDefinitionId] = useState('')
    const formDefinitions = useAppSelector(selectFormDefinitions)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<APIError | null>(null)

    const sectionTypes: Set<DocSectionType> = useMemo(() => {
        let types = new Set<DocSectionType>()
        for (let i = 0; i < allSections.length; i++) {
            const s = allSections[i];
            if (!types.has(s.type)) {
                types.add(s.type)
            }
        }
        return types
    }, [allSections])

    const availableSections = useMemo(() => {
        const definitions = getSectionDefinitionsForDocumentType(docState.header.type)
        return definitions.filter(def => !def.singleInDocument || !sectionTypes.has(def.type))
    }, [docState.header.type, sectionTypes])

    useEffect(() => {
        const hasCurrentType = availableSections.some(def => def.type === sectionType)
        if (!hasCurrentType && availableSections.length > 0) {
            setSectionType(availableSections[0].type)
        }
    }, [availableSections, sectionType])

    const handleOkClick = async () => {
        if (sectionType === 'FORM' && formDefinitionId === '') return
        if (sectionType && name) {
            setSaving(true)
            try {
                if (sectionType === 'FORM') {
                    // One server call creates the section together with its copy of the definition.
                    await dispatch(createFormSection({ documentId, name, parentSectionId, definitionId: formDefinitionId })).unwrap()
                } else {
                    await dispatch(createDocSection({
                        code: canSetCode ? code : '',
                        description: '',
                        name,
                        parentSectionId,
                        type: sectionType,
                        documentId,
                    })).unwrap()
                }
                dialog({ tag: 'none' })
            } catch (error) {
                setError(error as APIError)
            } finally {
                setSaving(false)
            }
        }
    }

    const handleCancelClick = () => {
        dialog({ tag: 'none' })
    }

    return <Dialog open={true} fullWidth maxWidth='xs'>
        <DialogTitle sx={{
            bgcolor: 'grey.100',
            borderBottom: '1px solid',
            borderColor: 'divider',
            py: 1.25
        }}>
            Nowa sekcja
        </DialogTitle>
        <DialogContent>
            <Box sx={{ py: 2, marginTop: 2 }}>
                <TextField
                    value={sectionType}
                    fullWidth
                    required={true}
                    select
                    label={<Localized id="doc-section-type"><span>_type</span></Localized>}
                    onChange={e => {
                        const selectedType = e.target.value as DocSectionType
                        setSectionType(selectedType)
                        setName(l10n.getString(sectionTypeValueToL10Code(selectedType), null, ""))

                    }}
                >
                    {availableSections.map(section => {
                        const label = SECTION_TYPE_LABELS[section.type]
                        return <MenuItem key={section.type} value={section.type}>
                            <Localized id={label.id}>{label.fallback}</Localized>
                        </MenuItem>
                    })}

                </TextField>
                {sectionType === 'FORM' && <Box sx={{ marginTop: 2 }}>
                    <FormDefinitionPicker
                        documentId={documentId}
                        value={formDefinitionId}
                        onChange={definitionId => {
                            const previous = formDefinitions.find(d => d.id === formDefinitionId)
                            const picked = formDefinitions.find(d => d.id === definitionId)
                            // Follow the definition's name until the user types their own.
                            if (picked && (!name || name === previous?.name || name === l10n.getString('doc-section-type-form', null, ''))) {
                                setName(picked.name)
                            }
                            setFormDefinitionId(definitionId)
                        }}
                    />
                </Box>}
                <TextField
                    sx={{
                        marginTop: 2,
                    }}
                    fullWidth
                    value={name}
                    onChange={e => { setName(e.target.value) }}
                    label={<Localized id='doc-section-name'>Nazwa</Localized>}
                    required={true}
                    error={!name}
                />
                {canSetCode && <TextField
                    sx={{
                        marginTop: 2,
                    }}
                    fullWidth
                    value={code}
                    onChange={e => { setCode(sanitizeSectionCode(e.target.value)) }}
                    label={<Localized id='document-section-report-code'>Kod raportu</Localized>}
                    helperText={<Localized id='document-section-report-code-hint'>{'Używany w szablonie DOCX jako {{SectionCode:KOD}}'}</Localized>}
                />}
            </Box>
        </DialogContent>
        <ServerErrorMsg err={error} />
        <DialogActions>
            <SaveButton loading={saving} onClick={handleOkClick} />
            <CancelButton loading={saving} onClick={handleCancelClick} />
        </DialogActions>
    </Dialog>
}

export const DocumentGuard = ({ children }: { children: ReactNode }) => {
    const dispatch = useAppDispatch()
    const { id: paramId } = useParams()
    const id = paramId ?? ''

    const [isLoading, setIsLoading] = useState<boolean | undefined>(undefined)
    const [isError, setIsError] = useState(false)
    const [apiError, setApiError] = useState<APIError | null>(null)

    useEffect(() => {
        let active = true
        let currentRequest: { abort: () => void } | null = null

        type AbortableRequest<T> = {
            unwrap: () => Promise<T>
            abort: () => void
        }

        const execute = async <T,>(
            name: string,
            request: AbortableRequest<T>
        ): Promise<T> => {
            currentRequest = request

            try {
                return await request.unwrap()
            } catch (error) {
                if (active) {
                    console.error(`[DocumentGuard] ${name} failed`, error)
                }

                throw error
            } finally {
                if (currentRequest === request) {
                    currentRequest = null
                }
            }
        }

        const fetchData = async () => {
            setIsLoading(true)
            setIsError(false)
            setApiError(null)

            try {
                const document = await execute(
                    "loadDocument",
                    dispatch(loadDocument(id))
                )

                if (!active) return

                await execute(
                    "loadDocumentActions",
                    dispatch(loadDocumentActions(id))
                )

                if (!active) return

                await execute(
                    "loadDocumentSections",
                    dispatch(loadDocumentSections(id))
                )

                if (!active) return

                await execute(
                    "loadDocComments",
                    dispatch(loadDocComments(id))
                )

                if (!active) return

                if (document.type === "URS") {
                    await execute(
                        "loadURSComments",
                        dispatch(loadURSComments(id))
                    )

                    if (!active) return
                }

                await execute(
                    "loadManyBlobs",
                    dispatch(loadManyBlobs([document.guid]))
                )

            } catch (error: unknown) {
                if (!active) {
                    return
                }

                console.error("[DocumentGuard] fetchData error", error)

                setIsError(true)

                if (error && typeof error === "object" && "kind" in error) {
                    setApiError(error as APIError)
                } else {
                    setApiError({ kind: "unknown" })
                }
            } finally {
                if (active) {
                    setIsLoading(false)
                }
            }
        }

        void fetchData()

        return () => {
            active = false
            currentRequest?.abort()
        }
    }, [id, dispatch])

    if (isError) {
        return (
            <ErrorContainer
                error={apiError ?? { kind: "unknown" }}
            />
        )
    }

    if (isLoading === true) {
        return <LoadingContainer />
    }

    if (isLoading === undefined) {
        return <>Waiting...</>
    }

    return <>{children}</>
}

export const DocumentFormInternal = () => {
    const dispatch = useAppDispatch()

    const docState = useAppSelector(selectDocument)?.doc ?? impossible()

    const document = docState.header
    //
    // const [actions, setActions] = useState(actionsDef)
    const workflowActions = docState.workflowActions ?? impossible()
    const allWorkflowActions = useAppSelector(docWfActionsSelectors.selectAll)
    const actions = useAppSelector(docActionsSelectors.selectAll) ?? impossible()
    const { header } = useAppSelector(selectDocument)?.doc ?? impossible()
    const editable = useAppSelector(isDocEditable)
    const isRA = header.type === 'RA'
    const isUrs = header.type === 'URS'
    const isTemplate = header.isTemplate === true
    const raSectionId = useAppSelector(state => sectionSelectors.selectAll(state).find(s => s.type === 'RISK_ANALYSIS')?.id)

    const isDynamicUrs = header.type === 'URS' && document.documentStructure === 'DYNAMIC'
    const isQmsControlled = document.isQmsControlled === true

    const requirementsSectionId = useAppSelector(state => sectionSelectors.selectAll(state).find(s => s.type === 'REQUIREMENTS')?.id)
    const [saving, setSaving] = useState(false)

    const [selectedNode, setSelectedNode] = useState<string | null>(null);
    const [tocCollapsed, setTocCollapsed] = useState(false)
    const [tab, setTab] = useState<DocumentFormTabs>(
        document.status === "R" ? "rejection" : "content"
    );
    const navigate = useNavigate()

    useEffect(() => {
        if (document.status === "R") {
            setTab("rejection")
        } else if (document.status !== "F") {
            setTab(current => current === "rejection" ? "content" : current)
        }
    }, [document.status])

    const handleTabChange = useCallback((_, newTab) => {
        setTab(newTab)
    }, [])
    const switchTab = useCallback((tab: DocumentFormTabs) => setTab(tab), [])

    const [selectedAction, setSelectedAction] = useState<DocumentWorkflowActions | null>(null);
    const [newVersionDialogOpen, setNewVersionDialogOpen] = useState(false);
    const [publishWizard, setPublishWizard] = useState(false);
    const [workflowTeamRefreshTrigger, setWorkflowTeamRefreshTrigger] = useState(0)

    const handleActionClick = (action: DocumentWorkflowActions) => {
        if (action.toUpperCase() === "PUBLISH") {
            setPublishWizard(true)
            return
        }
        setSelectedAction(action);
    }

    const getButtonColor = (action: DocumentWorkflowActions) => {
        switch (action) {
            case "Reject":
            case "WithdrawFromOpinion":
                return "error";

            case "ResendToOpinion":
                return "warning";

            default:
                return "info";
        }
    }

    const onWorkflowActionSuccess = () => {
        setSelectedAction(null)
        setWorkflowTeamRefreshTrigger(current => current + 1)
    }

    const onNewVersionSuccess = (docId: AppId) => () => {
        setNewVersionDialogOpen(false)
        navigate(`/documents/edit/${docId}`)
    }

    const handleCancelPublishClick = useCallback(() => {
        setPublishWizard(false)
    }, [setPublishWizard])

    const handleConfirmPublishClick = async (
        method: "vchainer" | "link",
        selectedCompanies: Set<AppId>,
        supplierIds: Set<AppId>
    ) => {
        setSaving(true)
        try {
            const result = await dispatch(publishAction({
                url: 'api/Urs/publish/' + document.id,
                method: method,
                companiesIds: Array.from(selectedCompanies),
                suppliersIds: Array.from(supplierIds)
            }))

            if (publishAction.fulfilled.match(result)) {
                dispatch(showSuccess("saved"))

            }
            else {
                let errorMsg = "error"
                if (result.payload) {
                    if (result.payload.kind === 'http') {
                        const problem = result.payload.problem
                        if (problem) {
                            errorMsg = problem.title
                        }
                    }
                }
                dispatch(showError(errorMsg))
            }
        }
        catch (error) {
            dispatch(showError("error"))
        }
        setPublishWizard(false)
        setSaving(false)

    }


    return <Container maxWidth={false} sx={{ padding: "2px", paddingLeft: "2px!important", paddingRight: "2px!important" }}>
        <EntityBreadcrumbs current={{ type: "document", id: document.id }} />
        <DocumentFormHeader doc={document} />
        <Grid container >
            <Grid item xs={12}>
                <TabContext value={tab}>
                    <TabList onChange={handleTabChange}>
                        {(document.status === "R" || document.status === "F") && <Tab
                            key={-1}
                            value="rejection"
                            label={<Localized id="document-rejection">Rejection</Localized>}
                        />}
                        <Tab
                            key={0}
                            value="content"
                            label={<Localized id="document">_Document</Localized>} />
                        <Tab
                            key={2}
                            value="details"
                            label={<Localized id="document-details">_details</Localized>} />
                        <Tab
                            key={3}
                            value="team"
                            label={<Localized id="document-team">Zespół</Localized>}
                        />
                        <Tab
                            key={4}
                            value="versions"
                            label={<Localized id="versions-and-changelog">_Versions</Localized>}
                        />
                        <Tab
                            key={5}
                            value="attachments"
                            label={<Localized id="attachments">Attachments</Localized>} />
                        {!isUrs && !isQmsControlled &&
                            <Tab
                                key={6}
                                value="validated-object"
                                label={<Localized id="process-validated-object">Walidowany obiekt</Localized>} />
                        }
                        {isRA && raSectionId && <Tab
                            key={7}
                            value="ra"
                            label={<Localized id="risk-analysis">Analiza ryzyka</Localized>} />}
                        {isDynamicUrs && requirementsSectionId && <Tab
                            key={8}
                            value="requirements"
                            label={<Localized id="doc-section-type-requirements">Wymagania</Localized>} />}

                        {isDynamicUrs && <Tab
                            key={9}
                            value="offers"
                            label={<Box sx={{ pr: 2 }}>
                                <Localized id="urs-published-urs">Opublikowane URS'Y</Localized>
                            </Box>}
                        />
                        }
                        {isQmsControlled && <Tab
                            key={53}
                            value="qms"
                            label={<Localized id="qms-details">QMS details</Localized>} />
                        }
                    </TabList>
                    {(document.status === "R" || document.status === "F") && <TabPanel sx={{ p: 1 }} value="rejection">
                        <DocumentRejectPanel
                            document={document}
                            onCreateNewVersion={() => setNewVersionDialogOpen(true)}
                            onSendToCorrection={() => handleActionClick("SendToCorrection")}
                        />
                    </TabPanel>}
                    <TabPanel sx={{ p: 1 }} value='content'>
                        <DocumentContent
                            switchTab={switchTab}
                            selectedNode={selectedNode}
                            setSelectedNode={setSelectedNode}
                            tocCollapsed={tocCollapsed}
                            setTocCollapsed={setTocCollapsed}
                        />
                    </TabPanel>
                    <TabPanel sx={{ p: 1 }} value='tests'>
                        <TestTable />
                    </TabPanel>
                    <TabPanel sx={{ p: 1 }} value="team">
                        {document.hasAdvancedWFL ? (
                            <DocumentTeamMembersView
                                documentId={document?.id}
                                documentStatus={document?.status}
                                refreshTrigger={workflowTeamRefreshTrigger}
                            />
                        ) : (
                            <TeamMemberView
                                documentId={document?.id}
                                actions={actions}
                            />
                        )}
                    </TabPanel>

                    <TabPanel sx={{ p: 1 }} value="versions">
                        <DocumentHistoryView documentId={document?.id} canEdit={editable} />
                    </TabPanel>
                    <TabPanel sx={{ p: 1 }} value={"details"}>
                        <DocumentDetailsPanel document={document} />
                    </TabPanel>
                    <TabPanel sx={{ p: 1 }} value="attachments">
                        <Attachements resourceId={document.guid} resourceType={BlobResourceType.Document} canDelete={editable} canUpload={editable} requireRemarks={remarksRequiredForDocumentStatus(document.status)} />
                    </TabPanel>
                    {!isUrs && !isQmsControlled &&
                        <TabPanel sx={{ p: 1 }} value="validated-object">
                            <ValidatedObjectCard />
                        </TabPanel>
                    }
                    {isRA && raSectionId && <TabPanel sx={{ p: 1 }} value="ra">
                        <RiskAnalysisSection sectionId={raSectionId} />
                    </TabPanel>}
                    {isDynamicUrs && requirementsSectionId && <TabPanel sx={{ p: 1 }} value="requirements">
                        <RequirementsSection sectionId={requirementsSectionId} />
                    </TabPanel>}
                    {isQmsControlled && <TabPanel sx={{ p: 1 }} value="qms">
                        <DocumentQMSDetails document={document} />
                    </TabPanel>}
                    <TabPanel sx={{ p: 1 }} value="offers">
                        <UrsOffersWrapper
                            ursId={document.id}
                            handleNewVersion={() => setNewVersionDialogOpen(true)}
                        />
                    </TabPanel>
                </TabContext>
            </Grid>
        </Grid>
        <AppBar position="fixed" color="primary" sx={{ top: 'auto', bottom: 0, p: 0.5 }}>
            <div className="flex justify-end">
                <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                    <Stack spacing={2} direction="row">
                        {allWorkflowActions.map((action) => (
                            <LoadingButton
                                key={action}
                                size="small"
                                variant="contained"
                                color={getButtonColor(action)}
                                onClick={() => handleActionClick(action)}
                            >
                                <ActionTitle action={action} />
                            </LoadingButton>
                        ))}
                    </Stack>
                </Box>
                {isUrs ?
                    <UrsDocumentToolbarMenu
                        documentId={document.id}
                        documentNo={document.no}
                        documentTitle={document.title}
                        language={document.language}
                        structureId={document.structureId}
                        advancedWFL={document.hasAdvancedWFL}
                    />
                    :
                    <DocumentToolbarMenu documentId={document.id} documentType={document.type} />
                }
                <Button onClick={() => navigate(-1)} variant='contained' color="primary" size="small"><Localized id='back'>Wróć</Localized></Button>
            </div>
        </AppBar>
        {
            selectedAction && (
                <WorkflowActionRunner
                    documentId={document.id}
                    action={selectedAction}
                    onSuccess={onWorkflowActionSuccess}
                    onCancel={() => setSelectedAction(null)} />)
        }
        {
            newVersionDialogOpen &&
            <DocumentNewVersionDialog
                documentId={document.id}
                onSuccess={onNewVersionSuccess}
                onCancel={() => setNewVersionDialogOpen(false)} />
        }
        {
            publishWizard && <WizardPublishDialog
                open={publishWizard}
                ursId={document.id}
                processing={saving}
                cancel={handleCancelPublishClick}
                confirm={handleConfirmPublishClick} />
        }
    </Container >
}

const ValidatedObjectCard = () => {
    const dispatch = useAppDispatch()

    const { validatedObject: vo, header } = useAppSelector(selectDocument)?.doc ?? impossible()

    const [err, setErr] = useState<APIError | null>(null)

    useEffect(() => {
        if (!vo) {
            dispatch(loadDocumentValidatedObject(header.id))
                .then(unwrapResult)
                .catch(err => setErr(err as APIError))
        }
    }, [])

    if (vo === null) return <LoadingContainer />

    if (err !== null) return <ServerErrorMsg err={err} />

    if (vo.type === 'NONE') {
        return <Box sx={{ p: 2 }}>
            <Localized id="validated-object-not-assigned">
                <span>_Nie przypisano przedmiotu walidacji do tego dokumentu.</span>
            </Localized>
        </Box>
    }
    if (vo.type === 'DEVICE') {
        return <ValidatedDeviceCard device={vo.data} />
    }

    if (vo.type === 'COMPUTERIZED_SYSTEM') {
        return <ValidatedComputerizedSystemCard system={vo.data} />
    }

    if (vo.type === 'ROOM') {
        return <ValidatedRoomCard />
    }

    return null
}

const ValidatedDeviceCard = (props: { device: Device }) => {
    const { device } = props
    return <Stack direction='column' spacing={2}>
        <DeviceHead device={device} />
        <DeviceGeneralInfo device={device} editable={false} />
    </Stack>
}

const nop = () => { }

const ValidatedComputerizedSystemCard = (props: { system: ComputerizedSystem }) => {
    const { system } = props
    return <Stack direction='column' spacing={2}>
        <ComputerizedSystemHead system={system} />
        <ComputerizedSystemOverviewPanel system={system} onUpdate={nop} onUpdated={nop} />
    </Stack>
}

const ValidatedRoomCard = () => {
    return <h3>Room</h3>
}

const UrsOffersWrapper = (props: { ursId: AppId, handleNewVersion: () => void }) => {
    const { ursId, handleNewVersion } = props
    const dispatch = useAppDispatch()

    const [accessRequests, setAccessRequests] = useState<PubAccessRequest[]>([])

    const accessRequestDone = useCallback((id: string) => {
        setAccessRequests(rs => rs.filter(r => r.requestId !== id))
    }, [])

    useEffect(() => {
        dispatch(loadAccessRequests({
            objectType: 'URS_REQUEST',
            objectId: ursId,
        })).then(unwrapResult)
            .then(reqs => setAccessRequests(reqs))
            .catch(err => console.log("Error loading access requests", err))
    }, [])

    return <UrsOffers
        ursId={ursId}
        handleNewVersion={handleNewVersion}
        accessRequests={accessRequests}
        accessRequestDone={accessRequestDone}
    />
}
